import { useState } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { DebugTabs } from '@/components/debug/DebugTabs';
import { WindowTabs, type BriefWindow } from '@/components/common/WindowTabs';
import { Button } from '@/components/ui/button';
import { useSession } from '@/lib/session';
import {
  revealDataApiKey,
  useCreateDataApiKey,
  useDataApiKeys,
  useDataApiStats,
  useDeleteDataApiKey,
  useRevokeDataApiKey,
  type DataApiConsumerStatDto,
  type DataApiEndpointStatDto,
  type DataApiKeyCreatedDto,
  type DataApiKeyRowDto,
  type DataApiStatsDto,
  type DataApiTrafficPointDto,
} from '../lib/api';

// WWD Data API console — admin-only. Two sections: per-consumer API key management
// (create shows the key ONCE; revoke is a tombstone) and an interactive tester that
// calls the REAL public machine route (/api/data/wwd/v1/ais/**, X-Api-Key header, no
// cookie) so a successful test here proves the exact path an external app will use.

type TabId = 'keys' | 'tester' | 'traffic';

export default function DataApi() {
  const { data: session } = useSession();
  // Admins and the scoped "dataapi" login both get the console (keys + tester).
  const canDataApi = !!session?.isAdmin || !!session?.isDataApi;
  const [tab, setTab] = useState<TabId>('keys');
  const [testerKey, setTesterKey] = useState('');

  const keys = useDataApiKeys(canDataApi);

  if (!canDataApi) {
    return (
      <Card>
        <CardTitle>Data API</CardTitle>
        <CardDescription className="mt-2">The Data API console requires the admin or data-API login.</CardDescription>
      </Card>
    );
  }

  const tabs = [
    { id: 'keys', label: 'API Keys', count: keys.data?.keys.filter((k) => !k.revokedAt).length ?? 0 },
    { id: 'tester', label: 'API Tester' },
    { id: 'traffic', label: 'Traffic' },
  ];

  return (
    <>
      <Card>
        <CardTitle>Data API</CardTitle>
        <CardDescription className="mt-2">
          Machine access to WWD data — AIS positions, fleet, equipment &amp; full rig specs — at{' '}
          <code className="font-mono text-xs">{window.location.origin}/api/data/wwd/v1/…</code>{' '}
          — authenticated by per-consumer <code className="font-mono text-xs">X-Api-Key</code>, read-only,
          rate-limited. <span className="text-accent">Admin-only</span>
        </CardDescription>
      </Card>

      <DebugTabs tabs={tabs} value={tab} onChange={(id) => setTab(id as TabId)} />

      {tab === 'keys' && (
        keys.isLoading ? <LoadingSkeleton lines={6} /> :
        keys.error ? <ErrorState message={(keys.error as Error).message} /> :
        <KeysSection
          keys={keys.data?.keys ?? []}
          onUseInTester={(k) => { setTesterKey(k); setTab('tester'); }}
        />
      )}

      {tab === 'tester' && <TesterSection apiKey={testerKey} onKeyChange={setTesterKey} />}

      {tab === 'traffic' && <TrafficSection />}
    </>
  );
}

/* ---------- Key management ---------- */

function KeysSection({
  keys,
  onUseInTester,
}: {
  keys: DataApiKeyRowDto[];
  onUseInTester: (key: string) => void;
}) {
  const create = useCreateDataApiKey();
  const revoke = useRevokeDataApiKey();
  const del = useDeleteDataApiKey();
  const [consumer, setConsumer] = useState('');
  const [note, setNote] = useState('');
  const [created, setCreated] = useState<DataApiKeyCreatedDto | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedRowId, setCopiedRowId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const copyRowKey = async (id: number) => {
    setRowError(null);
    try {
      const { key } = await revealDataApiKey(id);
      await navigator.clipboard.writeText(key);
      setCopiedRowId(id);
      setTimeout(() => setCopiedRowId((cur) => (cur === id ? null : cur)), 2500);
    } catch (e) {
      setRowError((e as Error).message);
    }
  };

  const submit = () => {
    if (!consumer.trim() || create.isPending) return;
    create.mutate(
      { consumer: consumer.trim(), note: note.trim() || undefined },
      {
        onSuccess: (dto) => {
          setCreated(dto);
          setCopied(false);
          setConsumer('');
          setNote('');
        },
      }
    );
  };

  const copyKey = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.key);
    setCopied(true);
  };

  return (
    <>
      <Card>
        <h2 className="text-xl font-semibold mb-1">Create key</h2>
        <p className="text-muted text-sm mb-4">
          One key per consuming app. Active keys can be copied again anytime from the table below —
          no need to regenerate. Two active keys per consumer are allowed (zero-downtime rotation).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Consumer (app/team)</span>
            <input
              className={inputClass}
              value={consumer}
              maxLength={64}
              placeholder="e.g. fleet-pulse"
              onChange={(e) => setConsumer(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Note (optional)</span>
            <input
              className={inputClass + ' w-72'}
              value={note}
              placeholder="purpose, contact, rotation plan"
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </label>
          <Button variant="primary" disabled={!consumer.trim() || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create key'}
          </Button>
        </div>
        {create.error && (
          <p className="mt-3 text-sm text-accent-danger">{(create.error as Error).message}</p>
        )}

        {created && (
          <div className="mt-4 rounded-(--radius-sm) border border-accent p-4">
            <p className="text-sm font-semibold">
              Key for <span className="text-accent">{created.consumer}</span> — shown once, copy it now:
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <code className="font-mono text-sm break-all select-all">{created.key}</code>
              <Button size="sm" onClick={copyKey}>{copied ? '✓ Copied' : 'Copy'}</Button>
              <Button size="sm" onClick={() => onUseInTester(created.key)}>Use in tester</Button>
              <Button size="sm" variant="danger" onClick={() => setCreated(null)}>Dismiss</Button>
            </div>
            <p className="mt-2 text-xs text-muted">
              You can copy it again later from the key table. Hand it to the consumer over a secure
              channel; it belongs in their server config, never in browser or mobile code.
            </p>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="text-xl font-semibold mb-3">
          Keys <span className="text-muted text-sm font-normal">(revoked keys stay listed until deleted — active keys must be revoked before delete)</span>
        </h2>
        {keys.length === 0 ? (
          <p className="text-muted">No keys yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Consumer</Th>
                  <Th>Key</Th>
                  <Th>Note</Th>
                  <Th>Created</Th>
                  <Th>Last used</Th>
                  <Th>Status</Th>
                  <Th right>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-border align-top hover:bg-surface-hover">
                    <td className="py-3 px-3 font-medium">{k.consumer}</td>
                    <td className="py-3 px-3 font-mono text-xs text-muted">{k.keyPrefix}…</td>
                    <td className="py-3 px-3 max-w-72 text-muted">{k.note ?? '—'}</td>
                    <td className="py-3 px-3 whitespace-nowrap text-muted">{fmtDate(k.createdAt)}</td>
                    <td className="py-3 px-3 whitespace-nowrap text-muted">{k.lastUsedAt ? fmtDate(k.lastUsedAt) : 'never'}</td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {k.revokedAt ? (
                        <span className="text-accent-danger">revoked {fmtDate(k.revokedAt)}</span>
                      ) : (
                        <span className="text-accent">active</span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-right whitespace-nowrap">
                      {k.canReveal && (
                        <Button size="sm" onClick={() => copyRowKey(k.id)}>
                          {copiedRowId === k.id ? '✓ Copied' : 'Copy key'}
                        </Button>
                      )}
                      {!k.revokedAt && !k.canReveal && (
                        <span className="text-muted text-xs mr-2" title="Minted before copy-anytime existed — regenerate once to make it copyable.">
                          not copyable
                        </span>
                      )}
                      {!k.revokedAt && (
                        <Button
                          size="sm"
                          variant="danger"
                          className="ml-2"
                          disabled={revoke.isPending}
                          onClick={() => {
                            if (window.confirm(`Revoke the '${k.consumer}' key ${k.keyPrefix}…? The consumer loses access within a minute.`)) {
                              revoke.mutate(k.id);
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                      {k.revokedAt && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={del.isPending}
                          onClick={() => {
                            if (window.confirm(`Permanently delete the revoked '${k.consumer}' key ${k.keyPrefix}…? This removes it from the list for good.`)) {
                              del.mutate(k.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {revoke.error && <p className="mt-3 text-sm text-accent-danger">{(revoke.error as Error).message}</p>}
      {del.error && <p className="mt-3 text-sm text-accent-danger">{(del.error as Error).message}</p>}
      {rowError && <p className="mt-3 text-sm text-accent-danger">{rowError}</p>}
      </Card>
    </>
  );
}

/* ---------- API tester ---------- */

type EndpointKind = 'positions' | 'rig' | 'lookup' | 'rigs' | 'rigDetail' | 'slugs' | 'photo';

// Shown under the endpoint picker — what the endpoint returns and how to use its params.
// "Slug" needs explaining once: it is WWD's stable, URL-safe identifier (lowercase-hyphenated,
// e.g. valaris-ds-15) — it survives renames, unlike display names, and is the id every
// detail endpoint and filter expects.
const ENDPOINT_HELP: Record<EndpointKind, string> = {
  positions:
    'Last-known AIS position per rig. Filter by freshness (since), licence tier (confidence: ' +
    'confirmed = BarentsWatch/NLOD, reported = scraped/community), source name, or a geographic ' +
    'box. Staleness is read from msgTimeUtc — positions are never erased, only aged.',
  rig:
    'One rig’s last-known position, addressed by its slug — WWD’s stable URL-safe id ' +
    '(lowercase-hyphenated, e.g. valaris-ds-15; survives renames, unlike the display name). ' +
    'Get slugs from /slugs, the fleet export, or resolve one via /ais/lookup.',
  lookup:
    'Resolve an external vessel identifier to a WWD rig: exact MMSI or IMO match, or a ' +
    'case-insensitive name search (q) that also covers former names. Returns the slug to use ' +
    'with the other endpoints.',
  rigs:
    'Full fleet export: card-level data + canonical equipment per rig (drawworks, top drive, ' +
    'mud pumps, BOP, heave compensator incl. brand rollups, hook load, depths, design class, ' +
    'name history). Filters take slugs/snake_case values — see /slugs for valid drillco ids.',
  rigDetail:
    'Everything WWD knows about one rig (by slug): the complete spec catalog with per-fact ' +
    'confidence (confirmed/estimated/reported/not_disclosed) and the cited source document + ' +
    'page, the latest-FSR contract book with dayrates, name history, and recent news.',
  slugs:
    'The id catalog: every rig slug (with name + drillco) and every drilling contractor slug ' +
    '(with rig count). A slug is the stable, URL-safe identifier (lowercase-hyphenated, e.g. ' +
    'deepsea-mira) — mint your lookups here, then call /rigs/{slug} or filter ?drillco=.',
  photo:
    'The rig’s vision-verified photo as a binary image (JPEG/PNG/WebP). 404 when no photo has ' +
    'been found yet — check hasPhoto on the fleet export first. Attribution comes as ' +
    'X-Photo-Credit / X-Photo-Page-Url headers (percent-encoded) and on the rig export fields.',
};

interface TestResponse {
  status: number;
  statusText: string;
  durationMs: number;
  headers: Record<string, string>;
  body: string;
  /** Object URL when the response was a binary image (photo endpoint) — rendered as <img>. */
  imageUrl?: string;
}

function TesterSection({ apiKey, onKeyChange }: { apiKey: string; onKeyChange: (k: string) => void }) {
  const [kind, setKind] = useState<EndpointKind>('positions');
  // positions params
  const [since, setSince] = useState('');
  const [confidence, setConfidence] = useState('');
  const [source, setSource] = useState('');
  const [bbox, setBbox] = useState('');
  // rig param
  const [slug, setSlug] = useState('');
  // lookup params
  const [mmsi, setMmsi] = useState('');
  const [imo, setImo] = useState('');
  const [q, setQ] = useState('');
  // fleet-export params
  const [fleetDrillco, setFleetDrillco] = useState('');
  const [fleetType, setFleetType] = useState('');
  const [fleetStatus, setFleetStatus] = useState('');

  const [pending, setPending] = useState(false);
  const [resp, setResp] = useState<TestResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const path = buildPath(kind, { since, confidence, source, bbox, slug, mmsi, imo, q, fleetDrillco, fleetType, fleetStatus });
  const url = `${window.location.origin}${path}`;

  const send = async () => {
    if (pending) return;
    setPending(true);
    setResp(null);
    setFailure(null);
    const started = performance.now();
    try {
      // credentials:'omit' — the machine route must work WITHOUT the portal cookie; testing
      // with the cookie attached would prove nothing about external access.
      const r = await fetch(path, { credentials: 'omit', headers: apiKey ? { 'X-Api-Key': apiKey } : {} });
      const headers: Record<string, string> = {};
      for (const name of ['content-type', 'cache-control', 'retry-after', 'x-photo-credit', 'x-photo-page-url', 'x-photo-official']) {
        const v = r.headers.get(name);
        if (v) headers[name] = name.startsWith('x-photo') ? decodeURIComponent(v) : v;
      }
      const contentType = r.headers.get('content-type') ?? '';
      let body: string;
      let imageUrl: string | undefined;
      if (r.ok && contentType.startsWith('image/')) {
        const blob = await r.blob();
        imageUrl = URL.createObjectURL(blob);
        body = `(binary image, ${blob.size.toLocaleString()} bytes, ${contentType})`;
      } else {
        body = prettyJson(await r.text());
      }
      setResp({
        status: r.status,
        statusText: r.statusText,
        durationMs: Math.round(performance.now() - started),
        headers,
        body,
        imageUrl,
      });
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Card>
        <h2 className="text-xl font-semibold mb-1">Request</h2>
        <p className="text-muted text-sm mb-4">
          Sent to the real public machine route — no portal cookie, the key is the only credential.
          A 200 here means an external app with this key gets the same response.
        </p>

        <div className="flex flex-wrap items-end gap-3 mb-3">
          <label className="flex flex-col gap-1">
            <span className={labelClass}>Endpoint</span>
            <select className={selectClass} value={kind} onChange={(e) => { setKind(e.target.value as EndpointKind); setResp(null); }}>
              <option value="positions">GET /ais/positions — fleet positions</option>
              <option value="rig">GET /ais/rigs/&#123;slug&#125; — single rig position</option>
              <option value="lookup">GET /ais/lookup — find by MMSI / IMO / name</option>
              <option value="rigs">GET /rigs — fleet export (equipment + card data)</option>
              <option value="rigDetail">GET /rigs/&#123;slug&#125; — full rig export (specs + contracts)</option>
              <option value="slugs">GET /slugs — id catalog (all rig + company slugs)</option>
              <option value="photo">GET /rigs/&#123;slug&#125;/photo — rig photo (binary)</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 grow max-w-xl">
            <span className={labelClass}>X-Api-Key</span>
            <input
              className={inputClass + ' w-full font-mono'}
              value={apiKey}
              placeholder="wwd_…  (create one under API Keys)"
              onChange={(e) => onKeyChange(e.target.value)}
            />
          </label>
        </div>

        <p className="text-muted text-sm mb-4 max-w-3xl">{ENDPOINT_HELP[kind]}</p>

        <div className="flex flex-wrap items-end gap-3">
          {kind === 'positions' && (
            <>
              <ParamInput label="since (ISO)" value={since} onChange={setSince} placeholder="2026-07-06T00:00:00Z" />
              <label className="flex flex-col gap-1">
                <span className={labelClass}>confidence</span>
                <select className={selectClass} value={confidence} onChange={(e) => setConfidence(e.target.value)}>
                  <option value="">any</option>
                  <option value="confirmed">confirmed (BarentsWatch/NLOD)</option>
                  <option value="reported">reported (aggregator)</option>
                </select>
              </label>
              <ParamInput label="source" value={source} onChange={setSource} placeholder="BarentsWatch" />
              <ParamInput label="bbox (minLon,minLat,maxLon,maxLat)" value={bbox} onChange={setBbox} placeholder="1.0,56.0,12.0,62.0" wide />
            </>
          )}
          {(kind === 'rig' || kind === 'rigDetail' || kind === 'photo') && (
            <ParamInput label="slug" value={slug} onChange={setSlug} placeholder="deepsea-atlantic" />
          )}
          {kind === 'rigs' && (
            <>
              <ParamInput label="drillco (company slug)" value={fleetDrillco} onChange={setFleetDrillco} placeholder="valaris" />
              <ParamInput label="type" value={fleetType} onChange={setFleetType} placeholder="semisub / jackup / drillship" />
              <ParamInput label="status" value={fleetStatus} onChange={setFleetStatus} placeholder="active / stacked / retired" />
            </>
          )}
          {kind === 'lookup' && (
            <>
              <ParamInput label="mmsi" value={mmsi} onChange={setMmsi} placeholder="257334000" />
              <ParamInput label="imo" value={imo} onChange={setImo} placeholder="8768236" />
              <ParamInput label="q (name search)" value={q} onChange={setQ} placeholder="deepsea" />
            </>
          )}
          <Button variant="primary" disabled={pending} onClick={send}>
            {pending ? 'Sending…' : '▶ Send'}
          </Button>
        </div>

        <div className="mt-4">
          <div className={labelClass}>Request URL</div>
          <code className="font-mono text-xs break-all">{url}</code>
          <div className={labelClass + ' mt-2'}>curl equivalent</div>
          <code className="font-mono text-xs break-all">
            curl -H "X-Api-Key: {apiKey ? apiKey.slice(0, 12) + '…' : '<key>'}" "{url}"
          </code>
        </div>
      </Card>

      {failure && <ErrorState message={failure} />}

      {resp && (
        <Card>
          <h2 className="text-xl font-semibold mb-3">
            Response{' '}
            <span className={resp.status < 400 ? 'text-accent' : 'text-accent-danger'}>
              {resp.status} {resp.statusText}
            </span>{' '}
            <span className="text-muted text-sm font-normal">in {resp.durationMs} ms</span>
          </h2>
          {Object.keys(resp.headers).length > 0 && (
            <div className="mb-3 text-xs font-mono text-muted">
              {Object.entries(resp.headers).map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
            </div>
          )}
          {resp.imageUrl && (
            <img
              src={resp.imageUrl}
              alt="rig photo response"
              className="mb-3 max-w-full max-h-96 rounded-(--radius-sm) border border-border"
            />
          )}
          <pre className="overflow-x-auto rounded-(--radius-sm) border border-border p-3 text-xs font-mono max-h-[32rem] overflow-y-auto whitespace-pre-wrap">
            {resp.body}
          </pre>
        </Card>
      )}
    </>
  );
}

function buildPath(
  kind: EndpointKind,
  p: {
    since: string; confidence: string; source: string; bbox: string; slug: string;
    mmsi: string; imo: string; q: string;
    fleetDrillco: string; fleetType: string; fleetStatus: string;
  }
): string {
  const base = '/api/data/wwd/v1';
  const qs = new URLSearchParams();
  if (kind === 'positions') {
    if (p.since.trim()) qs.set('since', p.since.trim());
    if (p.confidence) qs.set('confidence', p.confidence);
    if (p.source.trim()) qs.set('source', p.source.trim());
    if (p.bbox.trim()) qs.set('bbox', p.bbox.trim());
    return `${base}/ais/positions${qs.size ? `?${qs}` : ''}`;
  }
  if (kind === 'rig') {
    return `${base}/ais/rigs/${encodeURIComponent(p.slug.trim() || '<slug>')}`;
  }
  if (kind === 'rigs') {
    if (p.fleetDrillco.trim()) qs.set('drillco', p.fleetDrillco.trim());
    if (p.fleetType.trim()) qs.set('type', p.fleetType.trim());
    if (p.fleetStatus.trim()) qs.set('status', p.fleetStatus.trim());
    return `${base}/rigs${qs.size ? `?${qs}` : ''}`;
  }
  if (kind === 'rigDetail') {
    return `${base}/rigs/${encodeURIComponent(p.slug.trim() || '<slug>')}`;
  }
  if (kind === 'slugs') {
    return `${base}/slugs`;
  }
  if (kind === 'photo') {
    return `${base}/rigs/${encodeURIComponent(p.slug.trim() || '<slug>')}/photo`;
  }
  if (p.mmsi.trim()) qs.set('mmsi', p.mmsi.trim());
  if (p.imo.trim()) qs.set('imo', p.imo.trim());
  if (p.q.trim()) qs.set('q', p.q.trim());
  return `${base}/ais/lookup${qs.size ? `?${qs}` : ''}`;
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/* ---------- Traffic dashboard ---------- */

function TrafficSection() {
  const [win, setWin] = useState<BriefWindow>('24h');
  const stats = useDataApiStats(win, true);

  return (
    <>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="text-xl font-semibold mb-1">Traffic</h2>
            <p className="text-muted text-sm">
              Requests to the public Data API — who is calling, which endpoints, error rate, and
              invalid-key attempts. Recorded per request (no log-ingestion lag); times are UTC.
            </p>
          </div>
          <WindowTabs value={win} onChange={setWin} />
        </div>
      </Card>

      {stats.isLoading ? (
        <LoadingSkeleton lines={6} />
      ) : stats.error ? (
        <ErrorState message={(stats.error as Error).message} />
      ) : stats.data ? (
        <TrafficBody data={stats.data} hourly={win === '24h'} />
      ) : null}
    </>
  );
}

function TrafficBody({ data, hourly }: { data: DataApiStatsDto; hourly: boolean }) {
  const t = data.totals;
  const empty = t.requests === 0;
  return (
    <>
      <Card>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi label="Requests" value={fmtInt(t.requests)} sub={windowLabel(data.window)} />
          <Kpi label="Active consumers" value={fmtInt(t.activeConsumers)} sub="with a valid key" />
          <Kpi
            label="Error rate"
            value={`${(t.errorRate * 100).toFixed(1)}%`}
            sub={`${fmtInt(t.errors)} of ${fmtInt(t.requests)}`}
            danger={t.errorRate > 0.1}
          />
          <Kpi
            label="Invalid-key attempts"
            value={fmtInt(t.invalidAttempts)}
            sub={`${fmtInt(t.rateLimited)} rate-limited`}
            danger={t.invalidAttempts > 0}
          />
        </div>
      </Card>

      <Card>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted">Requests over time</h3>
        {empty ? (
          <p className="text-muted text-sm">No requests recorded in this window yet.</p>
        ) : (
          <TrafficChart points={data.series} hourly={hourly} />
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <ConsumersTable rows={data.consumers} />
        <EndpointsTable rows={data.endpoints} />
      </div>
    </>
  );
}

function Kpi({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div className="rounded-(--radius-sm) border border-border bg-surface-elevated p-4">
      <div className={labelClass}>{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${danger ? 'text-accent-danger' : 'text-text'}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-muted">{sub}</div>}
    </div>
  );
}

// Hand-rolled SVG (the WWD/MNA idiom — no chart lib): one bar per bucket, the error portion
// (4xx/5xx) stacked in danger at the base, successes in accent above it.
function TrafficChart({ points, hourly }: { points: DataApiTrafficPointDto[]; hourly: boolean }) {
  const W = 720;
  const H = 220;
  const M = { top: 12, right: 12, bottom: 28, left: 40 };
  const PW = W - M.left - M.right;
  const PH = H - M.top - M.bottom;
  const baseline = M.top + PH;

  const max = Math.max(1, ...points.map((p) => p.requests));
  const n = points.length;
  const slot = PW / Math.max(1, n);
  const barW = Math.max(1, Math.min(slot * 0.7, 28));
  const y = (v: number) => baseline - (v / max) * PH;
  const labelIdx = new Set<number>([0, Math.floor((n - 1) / 2), n - 1].filter((i) => i >= 0));

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full min-w-[32rem]" role="img" aria-label="Requests over time">
        {[0, max].map((v) => (
          <g key={v}>
            <line x1={M.left} y1={y(v)} x2={M.left + PW} y2={y(v)} stroke="var(--border)" strokeOpacity={0.5} />
            <text x={M.left - 6} y={y(v) + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">
              {v}
            </text>
          </g>
        ))}
        {points.map((p, i) => {
          const x = M.left + slot * i + slot / 2 - barW / 2;
          const reqPix = (p.requests / max) * PH;
          const errPix = (p.errors / max) * PH;
          const okPix = Math.max(0, reqPix - errPix);
          return (
            <g key={p.bucketUtc}>
              <title>{`${fmtBucket(p.bucketUtc, hourly)} — ${p.requests} req${p.errors ? `, ${p.errors} err` : ''}`}</title>
              {okPix > 0 && <rect x={x} y={baseline - reqPix} width={barW} height={okPix} fill="var(--accent)" rx={1} />}
              {errPix > 0 && <rect x={x} y={baseline - errPix} width={barW} height={errPix} fill="var(--accent-danger)" rx={1} />}
            </g>
          );
        })}
        <line x1={M.left} y1={baseline} x2={M.left + PW} y2={baseline} stroke="var(--border-strong)" />
        {points.map((p, i) =>
          labelIdx.has(i) ? (
            <text key={`l${i}`} x={M.left + slot * i + slot / 2} y={H - 8} textAnchor="middle" fontSize={11} fill="var(--text-muted)">
              {fmtBucket(p.bucketUtc, hourly)}
            </text>
          ) : null
        )}
      </svg>
      <div className="mt-2 flex items-center gap-4 text-xs text-muted">
        <Legend color="var(--accent)" label="Successful" />
        <Legend color="var(--accent-danger)" label="Errors (4xx/5xx)" />
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ background: color }} />
      {label}
    </span>
  );
}

function ConsumersTable({ rows }: { rows: DataApiConsumerStatDto[] }) {
  return (
    <Card>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted">By consumer</h3>
      {rows.length === 0 ? (
        <p className="text-muted text-sm">No traffic yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Consumer</Th>
                <Th right>Requests</Th>
                <Th right>Errors</Th>
                <Th right>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={`${c.consumer}|${c.invalid}`} className="border-b border-border hover:bg-surface-hover">
                  <td className="py-2 px-3 font-medium">
                    {c.invalid ? (
                      <span className="text-accent-danger" title="Unauthenticated attempts (no/invalid key)">
                        {c.consumer}
                      </span>
                    ) : (
                      c.consumer
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtInt(c.requests)}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${c.errors > 0 ? 'text-accent-danger' : 'text-muted'}`}>
                    {fmtInt(c.errors)}
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-muted">
                    {c.lastSeenUtc ? fmtDate(c.lastSeenUtc) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function EndpointsTable({ rows }: { rows: DataApiEndpointStatDto[] }) {
  const max = Math.max(1, ...rows.map((r) => r.requests));
  return (
    <Card>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted">By endpoint</h3>
      {rows.length === 0 ? (
        <p className="text-muted text-sm">No traffic yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-accent">
                <Th>Endpoint</Th>
                <Th right>Requests</Th>
                <Th right>Errors</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.endpoint} className="border-b border-border hover:bg-surface-hover">
                  <td className="py-2 px-3">
                    <div className="font-mono text-xs">{e.endpoint}</div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-hover">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${(e.requests / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right align-top tabular-nums">{fmtInt(e.requests)}</td>
                  <td className={`py-2 px-3 text-right align-top tabular-nums ${e.errors > 0 ? 'text-accent-danger' : 'text-muted'}`}>
                    {fmtInt(e.errors)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function fmtInt(n: number): string {
  return n.toLocaleString();
}

function windowLabel(w: string): string {
  return w === '7d' ? 'last 7 days' : w === '30d' ? 'last 30 days' : 'last 24 hours';
}

function fmtBucket(iso: string, hourly: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return hourly ? d.toISOString().slice(11, 16) : d.toISOString().slice(5, 10);
}

/* ---------- shared bits (Debug.tsx idioms) ---------- */

const labelClass = 'text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)';

const inputClass =
  'bg-surface-elevated border border-border rounded-(--radius-sm) px-3 py-2 text-sm min-h-11 ' +
  'font-(family-name:--font-body) focus:outline-2 focus:outline-offset-2 focus:outline-accent';

const selectClass = inputClass;

function ParamInput({
  label,
  value,
  onChange,
  placeholder,
  wide,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  wide?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={labelClass}>{label}</span>
      <input
        className={inputClass + (wide ? ' w-80' : '')}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui) ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 16).replace('T', ' ');
}
